import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import VanillaContextMenu from 'vanilla-context-menu';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import SetPreparationTimeDrawer from 'components/drawers/kitchen-capacity-drawers/set-preparation-time-drawer/set-preparation-time-drawer.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import CustomHeaderToolbar from './custom-header-toolbar/custom-header-toolbar.component';
import preparationTimeEvent from './preparation-time-event/preparation-time-event.component';
import { useKitchenCapacityContext } from '../kitchen-capacity.context';
import { Stack, useTheme } from '@mui/material';
import '../index.css';
import { convertToSetPreparationTimeFormData } from './preparation-time.utils';
import StandardHoursList from './standard-hours-list/standard-hours-list.component';
import FormSection from 'components/form-section/form-section.component';

export const PreparationTimeCalendar = () => {
  const { t } = useTranslation();
  const {
    preparationTime: {
      preparationTimeEvents,
      preparationTimeRef,
      addDummyEvent,
      eventsSet,
    },
  } = useKitchenCapacityContext();

  const setPreparationTimeDrawer = useUtilityDrawer();

  const theme = useTheme();

  const root = document.documentElement;
  root.style.setProperty(
    '--fc-page-bg-color',
    theme.palette.background.default,
  );

  root.style.setProperty(
    '--context-menu-item-text-color',
    theme.palette.text.primary,
  );

  root.style.setProperty(
    '--context-menu-item-hover-bg-color',
    theme.palette.action.hover,
  );

  const handleEventOverlap = (stillEvent, movingEvent) =>
    ['background', 'none'].includes(stillEvent.display) ||
    stillEvent.extendedProps.orderType !== movingEvent.extendedProps.orderType;

  return (
    <Stack direction="row" gap={2}>
      <Stack minWidth={320} sx={{ display: { xs: 'none', md: 'block' } }}>
        <StandardHoursList />
      </Stack>

      <FormSection
        title={t('KitchenCapacity.preparationTime.calendar.formSection.title')}
        subtitle={t(
          'KitchenCapacity.preparationTime.calendar.formSection.subtitle',
        )}
        sx={{ maxWidth: 'auto' }}
        contentSx={{ padding: '0 16px 16px 16px' }}
      >
        <Stack data-testid="PreparationTime.Calendar">
          <CustomHeaderToolbar />
          <FullCalendar
            plugins={[timeGridPlugin, interactionPlugin, listPlugin]}
            initialView="timeGridWeek" // Set initial view to Week
            now={DateTime.local().toJSDate()}
            initialEvents={preparationTimeEvents}
            eventOrder="-title" // otherwise events can swap columns and we don't want that
            eventsSet={eventsSet}
            allDaySlot={false}
            headerToolbar={false}
            editable
            eventResizableFromStart
            ref={preparationTimeRef}
            eventOverlap={handleEventOverlap}
            dayHeaderFormat={{ weekday: 'short' }}
            dateClick={dateSelect => {
              const start = DateTime.fromJSDate(dateSelect.date);
              const end = start.plus({ minutes: 15 });

              const dummyEvent = addDummyEvent(start, end);

              setPreparationTimeDrawer.toggleWithData(
                convertToSetPreparationTimeFormData({
                  title: dummyEvent.title,
                  start,
                  end,
                  throttleSettings: dummyEvent.extendedProps.throttleSettings,
                }),
              );
            }}
            eventClick={({ event }) => {
              if (!['background', 'none'].includes(event.display)) {
                setPreparationTimeDrawer.toggleWithData(
                  convertToSetPreparationTimeFormData({
                    id: event.id,
                    title: event.title,
                    start: DateTime.fromJSDate(event.start),
                    end: DateTime.fromJSDate(event.end),
                    throttleSettings: event.extendedProps.throttleSettings,
                  }),
                );
              }
            }}
            scrollTime="00:00:00"
            slotDuration="00:15:00"
            eventDidMount={({ event, el }) => {
              if (!['background', 'none'].includes(event.display)) {
                new VanillaContextMenu({
                  scope: el,
                  normalizePosition: false,
                  transitionDuration: 0,
                  customClass: 'context-menu-custom',
                  customThemeClass: 'context-menu-item-custom',
                  menuItems: [
                    {
                      label: t(
                        'KitchenCapacity.preparationTime.calendar.event.menuItems.edit',
                      ),
                      callback: () => {
                        setPreparationTimeDrawer.toggleWithData(
                          convertToSetPreparationTimeFormData({
                            id: event.id,
                            title: event.title,
                            start: DateTime.fromJSDate(event.start),
                            end: DateTime.fromJSDate(event.end),
                            throttleSettings:
                              event.extendedProps.throttleSettings,
                          }),
                        );
                      },
                    },
                    'hr',
                    {
                      label: t(
                        'KitchenCapacity.preparationTime.calendar.event.menuItems.delete',
                      ),
                      callback: () => {
                        event.remove();
                      },
                    },
                  ],
                });
              }
            }}
            eventContent={preparationTimeEvent}
            height="auto"
            listDaySideFormat={false}
          />
          <SetPreparationTimeDrawer {...setPreparationTimeDrawer} />
        </Stack>
      </FormSection>
    </Stack>
  );
};
export default PreparationTimeCalendar;
